import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Robot, Play, Check, X, Timer, ClockCounterClockwise, Queue, Lightning, Brain, Trash, Copy, Stamp, Info, CaretDown, ChatCircleDots, PaperPlaneRight } from "@phosphor-icons/react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, inr, formatApiErrorDetail } from "@/lib/api";

const STATUS_STYLES = {
  pending: "bg-warning/10 text-[#b07600] border-warning/30",
  executed: "bg-success/10 text-[#00953e] border-success/25",
  auto_executed: "bg-success/10 text-[#00953e] border-success/25",
  rejected: "bg-[#F3F4F6] text-[#525866] border-[#E5E7EB]",
  expired: "bg-[#F3F4F6] text-[#8a90a0] border-[#E5E7EB]",
  superseded: "bg-[#F3F4F6] text-[#8a90a0] border-[#E5E7EB]",
  failed: "bg-red-50 text-red-500 border-red-200",
  blocked: "bg-red-50 text-red-600 border-red-300",
  active: "bg-[#0052FF]/8 text-[#0052FF] border-[#0052FF]/20",
  triggered: "bg-success/10 text-[#00953e] border-success/25",
  expired: "bg-[#F3F4F6] text-[#8a90a0] border-[#E5E7EB]",
  cancelled: "bg-[#F3F4F6] text-[#8a90a0] border-[#E5E7EB]",
};

const StatusBadge = ({ value }) => (
  <Badge className={`rounded-full font-bold text-[9px] uppercase border ${STATUS_STYLES[value] || STATUS_STYLES.expired}`}>
    {(value || "").replace(/_/g, " ")}
  </Badge>
);

const actionLabel = (a) => {
  if (a.type === "scale_budget") return `Set budget ${inr(a.params.from_budget)} → ${inr(a.params.to_budget)}/day`;
  if (a.type === "pause_campaign") return "Pause campaign";
  if (a.type === "scale_adset_budget") return `Ad set "${a.params.adset_name}" — budget ${inr(a.params.from_budget)} → ${inr(a.params.to_budget)}/day`;
  if (a.type === "pause_adset") return `Pause ad set "${a.params.adset_name}"`;
  if (a.type === "duplicate_ad") return `Duplicate winning ad "${a.params.ad_name}" (PAUSED copy)`;
  if (a.type === "generate_creative") return "Generate new AI ad creative (image + copy)";
  return a.params?.title || "Manual task";
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

const SerialChip = ({ serial, id }) => serial ? (
  <button onClick={() => { navigator.clipboard.writeText(serial); toast.success(`${serial} copied — paste it in Meera chat`); }}
    data-testid={`serial-chip-${id}`} title="Card ID — click to copy, then reference it with Meera"
    className="rounded-md bg-[#F3F4F6] hover:bg-ai/10 hover:text-ai border border-[#E5E7EB] px-1.5 py-0.5 text-[10px] font-mono font-bold text-[#525866] transition-colors shrink-0">
    {serial}
  </button>
) : null;

const MeeraChat = ({ onQueueChanged, prefill }) => {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (prefill) { setOpen(true); setInput(prefill.text); }
  }, [prefill]);

  useEffect(() => {
    if (open && msgs === null) {
      api.get("/meera/chat").then(({ data }) => setMsgs(data.messages || [])).catch(() => setMsgs([]));
    }
  }, [open, msgs]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, sending]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setSending(true);
    setMsgs((m) => [...(m || []), { role: "user", text, at: new Date().toISOString() }]);
    try {
      const { data } = await api.post("/meera/chat", { message: text });
      setMsgs((m) => [...m, { role: "meera", text: data.reply, at: new Date().toISOString(), tools: data.tools_used }]);
      if (data.queue_changed) onQueueChanged?.();
    } catch (e) {
      const detail = e.response?.data?.detail || "Meera is unavailable right now — try again.";
      setMsgs((m) => [...m, { role: "meera", text: `⚠ ${detail}`, at: new Date().toISOString() }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="meera-chat-button"
        className={`fixed bottom-6 right-6 z-40 flex items-center gap-2 rounded-full bg-[#0A0D14] text-white pl-4 pr-5 py-3 shadow-xl hover:bg-[#1c2230] transition-colors ${open ? "hidden" : ""}`}>
        <ChatCircleDots size={20} weight="fill" className="text-[#FF6B35]" />
        <span className="text-sm font-bold">Ask Meera</span>
      </button>
      {open && (
        <div data-testid="meera-chat-panel"
          className="fixed bottom-6 right-6 z-50 w-[400px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-6rem)] bg-white border border-[#E5E7EB] rounded-2xl shadow-2xl flex flex-col overflow-hidden">
          <div className="flex items-center gap-2.5 px-4 py-3 bg-[#0A0D14] text-white shrink-0">
            <div className="w-8 h-8 rounded-full bg-[#FF6B35] flex items-center justify-center font-bold text-sm">M</div>
            <div className="min-w-0">
              <div className="text-sm font-bold">Meera</div>
              <div className="text-[10px] text-white/60">Senior manager copilot · can act on the queue · 1 credit per message</div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="meera-chat-close" className="ml-auto opacity-70 hover:opacity-100"><X size={16} weight="bold" /></button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-[#FAFAFB]">
            {msgs === null ? (
              <p className="text-xs text-[#8a90a0]">Loading…</p>
            ) : msgs.length === 0 ? (
              <div className="text-xs text-[#525866] bg-white border border-[#E5E7EB] rounded-xl p-3">
                Hi, I'm Meera. Every queue card has an ID like <b>AQ-2026-003</b> — quote it and I'll know exactly which card you mean. I can explain any card, approve / reject / mark them done, clean up duplicates, check live campaign numbers, or queue new optimizations. Try: <i>"explain AQ-2026-003"</i> or <i>"approve AQ-2026-003, reject the rest"</i>
              </div>
            ) : (
              msgs.map((m, i) => (
                <div key={i} data-testid={`meera-msg-${i}`}
                  className={`max-w-[85%] rounded-xl px-3 py-2 text-xs leading-relaxed whitespace-pre-wrap ${
                    m.role === "user" ? "ml-auto bg-[#0A0D14] text-white" : "bg-white border border-[#E5E7EB] text-[#0A0D14]"}`}>
                  {m.text}
                  {m.role === "meera" && (m.tools || []).length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {[...new Set(m.tools)].map((t) => (
                        <span key={t} className="rounded-full bg-[#FF6B35]/10 text-[#c2410c] px-1.5 py-0.5 text-[9px] font-bold uppercase">{t.replace(/_/g, " ")}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
            {sending && <div className="bg-white border border-[#E5E7EB] rounded-xl px-3 py-2 text-xs text-[#8a90a0] w-fit">Meera is working<span className="animate-pulse">…</span></div>}
            <div ref={endRef} />
          </div>
          <div className="p-3 border-t border-[#E5E7EB] bg-white flex gap-2 shrink-0">
            <Input value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
              placeholder='e.g. "explain AQ-2026-003" or "approve AQ-2026-003, reject the rest"'
              data-testid="meera-chat-input" className="h-9 text-xs rounded-full" disabled={sending} />
            <Button onClick={send} disabled={sending || !input.trim()} data-testid="meera-chat-send"
              className="rounded-full h-9 w-9 p-0 bg-[#FF6B35] hover:bg-[#e55a2b] shrink-0">
              <PaperPlaneRight size={14} weight="fill" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

const HowItWorks = ({ maxPct, minLeads }) => {
  const [open, setOpen] = useState(false);
  const RULES = [
    ["Workflow", [
      "The agent runs automatically after every 3-hour Meta sync (or on Run Agent Now), powered by Claude with live tools + persistent memory.",
      "It refreshes campaign / ad set / ad statuses straight from Meta before every run — it never acts on stale data.",
      "It analyzes ONLY campaigns that are ACTIVE and actually delivering over the last 14 days.",
      "Active campaigns that spent under ₹100 in 14 days are grouped into ONE 'not delivering' digest card instead of individual suggestions.",
      "Every proposal lands in this Approval Queue — in Co-pilot mode nothing touches Facebook without a human decision. Auto-pilot executes only within guardrails.",
    ]],
    ["Guardrails & hygiene", [
      `Budget changes are hard-capped at ±${maxPct}% per action (campaign or ad set).`,
      "Campaign structure is validated first (mixed goals / CBO conflicts are blocked — Meta error #1885760 prevention).",
      `Campaigns with fewer than ${minLeads} leads never get executable actions — only advisory tasks.`,
      "One advisory card per campaign: a newer suggestion replaces the older one instead of stacking.",
      "Cards not acted on within 7 days auto-expire to the Audit Log.",
    ]],
    ["Who can act", [
      "Owner and members with 'Can publish & approve' can Approve & Execute.",
      "Other members can Recommend to owner — their endorsement shows on the card and in the Sign-off History.",
      "Every decision is recorded in the Activity Log with name and timestamp.",
    ]],
  ];
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg" data-testid="agent-how-it-works">
      <button onClick={() => setOpen((o) => !o)} data-testid="how-it-works-toggle"
        className="w-full flex items-center gap-2 px-5 py-3.5 text-left">
        <Info size={16} weight="duotone" className="text-[#0052FF] shrink-0" />
        <span className="text-sm font-bold text-[#0A0D14]">How the AdPilot Agent works — rules & workflow</span>
        <CaretDown size={14} weight="bold" className={`ml-auto text-[#8a90a0] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 grid md:grid-cols-3 gap-5 border-t border-[#E5E7EB]/60 pt-4">
          {RULES.map(([title, items]) => (
            <div key={title}>
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8a90a0]">{title}</div>
              <ul className="mt-2 space-y-1.5">
                {items.map((r, i) => (
                  <li key={i} className="text-[11.5px] text-[#525866] leading-relaxed flex gap-1.5">
                    <span className="text-[#0052FF] font-bold shrink-0">·</span>{r}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function Automation() {
  const [settings, setSettings] = useState(null);
  const [actions, setActions] = useState([]);
  const [rules, setRules] = useState([]);
  const [runs, setRuns] = useState([]);
  const [memories, setMemories] = useState([]);
  const [running, setRunning] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [meeraPrefill, setMeeraPrefill] = useState(null);
  const { user } = useAuth();
  const isOwner = user?.role === "owner" || user?.role === "admin" || !!user?.can_publish;
  const [histFilter, setHistFilter] = useState("all");

  const load = useCallback(() => {
    api.get("/agent/settings").then(({ data }) => setSettings(data)).catch(() => {});
    api.get("/agent/actions").then(({ data }) => setActions(data)).catch(() => {});
    api.get("/agent/rules").then(({ data }) => setRules(data)).catch(() => {});
    api.get("/agent/runs").then(({ data }) => setRuns(data)).catch(() => {});
    api.get("/agent/memory").then(({ data }) => setMemories(data.memories || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveSettings = async (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    try {
      const { data } = await api.put("/agent/settings", next);
      setSettings(data);
      toast.success("Agent settings saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/agent/run");
      toast.success(`Agent run complete — ${data.actions_created} action(s), ${data.rules_created} watch rule(s), ${data.rules_triggered} rule(s) triggered`, { duration: 8000 });
      if (data.summary) toast.info(data.summary, { duration: 10000 });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail), { duration: 8000 });
    } finally {
      setRunning(false);
    }
  };

  const decide = async (a, verb) => {
    setBusyId(a.id);
    try {
      await api.post(`/agent/actions/${a.id}/${verb}`);
      toast.success(verb === "approve" ? (a.type === "human_task" ? "Marked done" : `Executed: ${actionLabel(a)}`)
        : verb === "recommend" ? "Recommended to the owner — they'll see your endorsement on this action"
        : "Action rejected");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail), { duration: 8000 });
    } finally {
      setBusyId(null);
      load();
    }
  };

  const cancelRule = async (r) => {
    try {
      await api.post(`/agent/rules/${r.id}/cancel`);
      toast.success("Watch rule cancelled");
      load();
    } catch (e) {
      toast.error("Cancel failed");
    }
  };

  const pending = actions.filter((a) => a.status === "pending");
  const history = actions.filter((a) => a.status !== "pending");
  const histFiltered = history.filter((a) => {
    if (histFilter === "all") return true;
    if (histFilter === "executed") return a.status === "executed" || a.status === "auto_executed";
    if (histFilter === "expired") return a.status === "expired" || a.status === "superseded";
    return a.status === histFilter;
  });
  const activeRules = rules.filter((r) => r.status === "active");

  if (!settings) return null;

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="ai-panel rounded-lg p-6" data-testid="agent-settings-card">
        <div className="flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-3">
            <span className="h-10 w-10 rounded-lg bg-ai/10 flex items-center justify-center"><Robot size={22} weight="duotone" className="text-ai" /></span>
            <div>
              <h2 className="font-heading text-base font-extrabold tracking-tight text-[#0A0D14]">AdPilot Agent</h2>
              <p className="text-[11px] text-[#8a90a0]">Runs after every 3h sync · Claude Opus 4.7 · tools + persistent memory</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={settings.enabled} onCheckedChange={(v) => saveSettings({ enabled: v })} data-testid="agent-enabled-switch" />
            <span className={`text-xs font-bold ${settings.enabled ? "text-[#00953e]" : "text-[#8a90a0]"}`}>{settings.enabled ? "ON" : "OFF"}</span>
          </div>
          <Select value={settings.mode} onValueChange={(v) => saveSettings({ mode: v })}>
            <SelectTrigger className="w-44 rounded-lg bg-white h-9" data-testid="agent-mode-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="copilot">Co-pilot (approve first)</SelectItem>
              <SelectItem value="autopilot">Auto-pilot (guardrailed)</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8a90a0]">Max budget Δ</span>
            <Input value={settings.max_budget_change_pct} data-testid="agent-maxpct-input"
              onChange={(e) => setSettings({ ...settings, max_budget_change_pct: Number(e.target.value.replace(/\D/g, "")) || 0 })}
              onBlur={() => saveSettings({})} className="h-9 w-16 rounded-lg font-mono text-sm text-center" />
            <span className="text-xs text-[#8a90a0]">%</span>
          </div>
          <Button onClick={runNow} disabled={running || !settings.enabled} data-testid="agent-run-button"
            className="ml-auto rounded-full bg-ai hover:bg-[#5457e0] text-white font-bold">
            <Play size={15} weight="fill" className="mr-1.5" /> {running ? "Agent thinking…" : "Run Agent Now"}
          </Button>
        </div>
        {!settings.enabled && <p className="mt-3 text-xs text-[#8a6400] bg-warning/5 border border-warning/30 rounded-lg px-3 py-2">Agent is OFF — enable it to start generating actions on every sync. Start in Co-pilot: nothing touches Facebook without your approval.</p>}
      </div>

      <HowItWorks maxPct={settings.max_budget_change_pct} minLeads={settings.min_leads_for_decision} />

      <Tabs defaultValue="queue">
        <TabsList className="bg-white border border-[#E5E7EB] rounded-lg p-1">
          <TabsTrigger value="queue" data-testid="tab-queue" className="rounded-md text-xs font-bold"><Queue size={14} className="mr-1.5" />Approval Queue ({pending.length})</TabsTrigger>
          <TabsTrigger value="rules" data-testid="tab-rules" className="rounded-md text-xs font-bold"><Timer size={14} className="mr-1.5" />Watch Rules ({activeRules.length})</TabsTrigger>
          <TabsTrigger value="memory" data-testid="tab-memory" className="rounded-md text-xs font-bold"><Brain size={14} className="mr-1.5" />Memory ({memories.length})</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history" className="rounded-md text-xs font-bold"><ClockCounterClockwise size={14} className="mr-1.5" />Audit Log</TabsTrigger>
        </TabsList>

        <TabsContent value="queue" className="mt-4 space-y-3">
          {pending.length === 0 ? (
            <div className="bg-white border border-[#E5E7EB] rounded-lg p-10 text-center text-sm text-[#8a90a0]" data-testid="queue-empty">
              Queue is empty. {settings.enabled ? "The agent will add actions after the next sync, or hit Run Agent Now." : "Enable the agent to get started."}
            </div>
          ) : pending.map((a) => (
            <div key={a.id} data-testid={`action-card-${a.id}`} className="bg-white border border-[#E5E7EB] rounded-lg p-5 flex items-start gap-4">
              <span className="h-9 w-9 shrink-0 rounded-lg bg-ai/10 flex items-center justify-center"><Lightning size={17} weight="duotone" className="text-ai" /></span>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <SerialChip serial={a.serial} id={a.id} />
                  <span className="font-heading text-sm font-extrabold text-[#0A0D14]">{actionLabel(a)}</span>
                  <StatusBadge value={a.status} />
                  {a.source === "watch_rule" && <Badge className="rounded-full font-bold text-[9px] uppercase border bg-ai/10 text-ai border-ai/25">from watch rule</Badge>}
                  {a.recommended_by && (
                    <Badge className="rounded-full font-bold text-[9px] uppercase border bg-[#0052FF]/10 text-[#0052FF] border-[#0052FF]/25" data-testid={`recommended-badge-${a.id}`}>
                      <Stamp size={10} weight="fill" className="mr-1" /> Recommended by {a.recommended_by}
                    </Badge>
                  )}
                </div>
                {a.campaign_name && <div className="text-xs font-semibold text-[#0052FF] mt-0.5">{a.campaign_name}</div>}
                <p className="text-xs text-[#525866] leading-relaxed mt-1.5">{a.reason}</p>
                <div className="text-[10px] text-[#8a90a0] mt-1.5">
                  Confidence {Math.round((a.confidence || 0) * 100)}% · {fmtDate(a.created_at)}
                </div>
                {a.guardrail_note && (
                  <div className={`mt-2 rounded-lg border px-3 py-2 text-[11px] font-semibold leading-relaxed ${
                    a.guardrail_note.includes("STRUCTURE GUARDRAIL") ? "border-red-200 bg-red-50 text-red-600" : "border-warning/40 bg-warning/10 text-[#8a6400]"}`}
                    data-testid={`guardrail-note-${a.id}`}>
                    ⚠️ {a.guardrail_note}
                  </div>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
                {(isOwner || a.type === "human_task") ? (
                  <Button size="sm" onClick={() => decide(a, "approve")} disabled={busyId === a.id} data-testid={`approve-action-${a.id}`}
                    className="rounded-full bg-[#0A0D14] hover:bg-[#1c2230] text-white font-bold text-xs h-9 px-4">
                    <Check size={14} weight="bold" className="mr-1" /> {a.type === "human_task" ? "Mark Done" : "Approve & Execute"}
                  </Button>
                ) : (
                  <Button size="sm" onClick={() => decide(a, "recommend")} disabled={busyId === a.id || !!a.recommended_by} data-testid={`recommend-action-${a.id}`}
                    className="rounded-full bg-[#0A0D14] hover:bg-[#1c2230] text-white font-bold text-xs h-9 px-4 disabled:opacity-60">
                    <Stamp size={14} weight="fill" className="mr-1" /> {a.recommended_by ? "Recommended" : "Recommend to owner"}
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => setMeeraPrefill({ text: `About ${a.serial || "this card"}: `, ts: Date.now() })}
                  data-testid={`ask-meera-${a.id}`} title={`Ask Meera about ${a.serial || "this card"}`}
                  className="rounded-full border-ai/30 text-ai hover:bg-ai/10 hover:text-ai font-bold text-xs h-9 px-3">
                  <ChatCircleDots size={14} weight="duotone" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => decide(a, "reject")} disabled={busyId === a.id} data-testid={`reject-action-${a.id}`}
                  className="rounded-full border-[#E5E7EB] text-[#525866] hover:bg-red-50 hover:text-red-500 font-bold text-xs h-9 px-3">
                  <X size={14} weight="bold" />
                </Button>
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="rules" className="mt-4 space-y-3">
          {rules.length === 0 ? (
            <div className="bg-white border border-[#E5E7EB] rounded-lg p-10 text-center text-sm text-[#8a90a0]" data-testid="rules-empty">
              No watch rules yet. The agent creates them for "monitor N days, then act" recommendations.
            </div>
          ) : rules.map((r) => (
            <div key={r.id} data-testid={`rule-card-${r.id}`} className="bg-white border border-[#E5E7EB] rounded-lg p-5 flex items-start gap-4">
              <span className="h-9 w-9 shrink-0 rounded-lg bg-[#0052FF]/10 flex items-center justify-center"><Timer size={17} weight="duotone" className="text-[#0052FF]" /></span>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-heading text-sm font-extrabold text-[#0A0D14]">
                    {r.metric.toUpperCase()} {r.operator} {r.metric === "cpl" || r.metric === "spend" ? inr(r.value) : r.value} for {r.for_days} days →{" "}
                    {r.then_action.type === "scale_budget" ? `set budget ${inr(r.then_action.to_budget)}/day` : "pause campaign"}
                  </span>
                  <StatusBadge value={r.status} />
                </div>
                <div className="text-xs font-semibold text-[#0052FF] mt-0.5">{r.campaign_name}</div>
                {r.reason && <p className="text-xs text-[#525866] mt-1.5">{r.reason}</p>}
                <div className="text-[10px] text-[#8a90a0] mt-1.5">Created {fmtDate(r.created_at)}{r.note ? ` · ${r.note}` : ""}</div>
              </div>
              {r.status === "active" && (
                <Button size="sm" variant="outline" onClick={() => cancelRule(r)} data-testid={`cancel-rule-${r.id}`}
                  className="rounded-full border-[#E5E7EB] text-[#525866] hover:bg-red-50 hover:text-red-500 font-bold text-xs h-8 px-3 shrink-0">
                  Cancel
                </Button>
              )}
            </div>
          ))}
        </TabsContent>

        <TabsContent value="memory" className="mt-4 space-y-3">
          <p className="text-[11px] text-[#8a90a0]">The agent's persistent memory — winning ads it tracks automatically plus learnings it saves during runs. It reads this before every decision, across sessions.</p>
          {memories.length === 0 ? (
            <div className="bg-white border border-[#E5E7EB] rounded-lg p-10 text-center text-sm text-[#8a90a0]" data-testid="memory-empty">
              No memories yet — they appear after the first agent run (winning ads are captured automatically).
            </div>
          ) : memories.map((m) => (
            <div key={m.id} data-testid={`memory-card-${m.id}`} className="bg-white border border-[#E5E7EB] rounded-lg px-5 py-3 flex items-center gap-3">
              <Badge className={`rounded-full font-bold text-[9px] uppercase border shrink-0 ${m.kind === "winner" ? "bg-success/10 text-[#00953e] border-success/25" : "bg-ai/10 text-ai border-ai/25"}`}>
                {m.kind === "winner" ? "Winner" : "Learning"}
              </Badge>
              <p className="text-xs text-[#525866] leading-relaxed flex-1 min-w-0">{m.content}</p>
              <span className="text-[10px] text-[#8a90a0] font-mono shrink-0">{fmtDate(m.updated_at)}</span>
              <button onClick={async () => { await api.delete(`/agent/memory/${m.id}`); load(); }} data-testid={`delete-memory-${m.id}`}
                className="text-[#8a90a0] hover:text-red-500 shrink-0"><Trash size={14} /></button>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="history" className="mt-4 space-y-5">
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
            <h3 className="font-heading text-sm font-extrabold text-[#0A0D14] mb-3">Recent agent runs</h3>
            {runs.length === 0 ? <p className="text-xs text-[#8a90a0]">No runs yet.</p> : (
              <div className="space-y-2">
                {runs.map((r) => (
                  <div key={r.id} className="flex items-start gap-3 text-xs border-b border-[#E5E7EB]/50 pb-2 last:border-0">
                    <StatusBadge value={r.status === "completed" ? "executed" : r.status} />
                    <span className="text-[#8a90a0] font-mono shrink-0">{fmtDate(r.started_at)}</span>
                    <span className="text-[#525866]">{r.trigger} · {r.actions_created} actions · {r.rules_created || 0} rules · {r.rules_triggered} triggered{r.tool_calls ? ` · ${r.tool_calls} tool call${r.tool_calls === 1 ? "" : "s"}` : ""}{r.summary ? ` — ${r.summary}` : ""}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          {history.length > 0 && (
            <div className="flex items-center gap-2" data-testid="history-filter-bar">
              <span className="text-[11px] font-bold text-[#525866]">Filter by state:</span>
              <Select value={histFilter} onValueChange={setHistFilter}>
                <SelectTrigger className="w-48 h-9 text-xs font-semibold bg-white" data-testid="history-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All states ({history.length})</SelectItem>
                  <SelectItem value="executed">✅ Executed / Done ({history.filter((a) => a.status === "executed" || a.status === "auto_executed").length})</SelectItem>
                  <SelectItem value="rejected">✕ Rejected ({history.filter((a) => a.status === "rejected").length})</SelectItem>
                  <SelectItem value="blocked">⛔ Blocked ({history.filter((a) => a.status === "blocked").length})</SelectItem>
                  <SelectItem value="failed">⚠️ Failed ({history.filter((a) => a.status === "failed").length})</SelectItem>
                  <SelectItem value="expired">🕓 Expired / Superseded ({history.filter((a) => a.status === "expired" || a.status === "superseded").length})</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {histFiltered.length === 0 ? (
            <div className="bg-white border border-[#E5E7EB] rounded-lg p-10 text-center text-sm text-[#8a90a0]" data-testid="history-empty">{history.length === 0 ? "No decided actions yet." : "No actions in this state."}</div>
          ) : histFiltered.map((a) => (
            <div key={a.id} data-testid={`history-action-${a.id}`} className="bg-white border border-[#E5E7EB] rounded-lg px-5 py-3 flex items-center gap-3">
              <StatusBadge value={a.status} />
              <SerialChip serial={a.serial} id={`hist-${a.id}`} />
              <div className="min-w-0 flex-1">
                <span className="text-sm font-semibold text-[#0A0D14]">{actionLabel(a)}</span>
                {a.campaign_name && <span className="text-xs text-[#0052FF] font-semibold ml-2">{a.campaign_name}</span>}
                {a.error && <div className="text-[11px] text-red-500 mt-0.5">{a.error}</div>}
              </div>
              <span className="text-[10px] text-[#8a90a0] font-mono shrink-0">{fmtDate(a.executed_at || a.created_at)}{a.decided_by ? ` · ${a.decided_by}` : ""}</span>
            </div>
          ))}
        </TabsContent>
      </Tabs>
      <MeeraChat onQueueChanged={load} prefill={meeraPrefill} />
    </div>
  );
}
